import angular from 'angular';
import app from 'app';
import $ from 'jquery';
import UA from '@collectivehealth/ch-user-agent';


// Class added to body when modals are open.
export const BODY_ACTIVE_CLASS = 'chModal--modalOpen';

// Class to apply to the intermediate DOM element created when using components.
export const COMPONENT_CLASS = 'chModal__component';

// Class to apply to the backdrop element.
export const BACKDROP_CLASS = 'chModal__backdrop';

// Wrapper class used for each active modal.
export const MODAL_CLASS = 'chModal__modal';

// Class added to modal elements when they are opened.
export const MODAL_OPEN_CLASS = 'open';

// Class added to modal elements when they are closed.
export const MODAL_CLOSE_CLASS = 'close';

// Class added to backdrop when modals are open.
export const BACKDROP_ACTIVE_CLASS = 'active';

// Class added to backdrop when all modals have been closed.
export const BACKDROP_INACTIVE_CLASS = 'inactive';

// Symbol descriptor to use for "close on click" events.
export const CLOSE_ON_CLICK = 'chModal.close.click';

// Symbol descriptor to use for "close on esc" events.
export const CLOSE_ON_ESC = 'chModal.close.esc';



app.provider('chModal', function () {
  // Default configuration.
  const CONFIG = {
    BODY_ACTIVE_CLASS,
    COMPONENT_CLASS,
    BACKDROP_CLASS,
    MODAL_CLASS,
    MODAL_OPEN_CLASS,
    MODAL_CLOSE_CLASS,
    BACKDROP_ACTIVE_CLASS,
    BACKDROP_INACTIVE_CLASS,
    CLOSE_ON_CLICK,
    CLOSE_ON_ESC
  };


  /**
   * Allows consumers to override default configuration options.
   *
   * @param  {object} userConfig - Configuration object.
   */
  this.config = userConfig => {
    Object.assign(CONFIG, userConfig);
  };


  // Service.
  this.$get = ($animate, $compile, $document, $injector, $q, $rootScope, $templateRequest, $window) => {

    /**
     * @private
     *
     * Registry for tracking open modals.
     *
     * @type {array}
     */
    let modalRegistry = [];


    /**
     * @private
     *
     * Track scroll position of the page when the backdrop is shown. This is
     * necessary because Mobile Safari does not honor 'offset: hidden' on the body
     * element, so we must apply a hack to compensate.
     *
     * See: https://github.com/twbs/bootstrap/issues/15852
     *
     * @type {number}
     */
    let yOffset = 0;


    /**
     * @private
     *
     * Computed width of the client's scrollbar.
     *
     * @type {number}
     */
    let scrollbarWidth = 0;


    /**
     * @private
     *
     * Reference to body.
     *
     * @type {object}
     */
    const body = $document.find('body');


    /**
     * @private
     *
     * Create a backdrop. This element will contain all open modals.
     *
     * @type {object}
     */
    const backdropEl = angular.element(`<div class="${CONFIG.BACKDROP_CLASS}">`);


    // ----- Private Methods ---------------------------------------------------

    /**
     * @private
     *
     * Converts a camel-case string into a kebab-case string.
     *
     * @param {string} camelCase - Input string.
     * @return {string} - Output string.
     */
    const kebabString = camelCase => camelCase.replace(/([A-Z])/g, char => '-' + char.toLowerCase());


    /**
     * Provided an object containing a set of CSS rules and a jQuery/jqLite
     * element, applies the styles to the element.
     *
     * @example
     *
     * applyStyle({
     *   display: 'block'
     * }, someEl);
     *
     * @param  {object} styles - CSS styles to apply.
     * @param  {object} el - Element to apply styles to.
     */
    function applyStyle (styles, el) {
      Object.keys(styles).forEach(property => {
        const rule = styles[property];
        el.css(property, rule);
      });
    }


    /**
     * @private
     *
     * Executes the provided function if the user's device is a mobile phone or
     * a tablet.
     *
     * @param {function} fn - Function to execute.
     * @return {*} - Return value of the function, if executed. Otherwise, returns
     *   undefined.
     */
    function mobileOnly (fn) {
      if (UA.device.type === 'mobile' || UA.device.type === 'tablet') {
        return fn();
      }
    }


    /**
     * @private
     *
     * Handles resizing modal container elements on mobile devices.
     */
    function mobileResizeHandler () {
      $([
        `.${CONFIG.MODAL_CLASS} > :first-child`,
        `.${CONFIG.MODAL_CLASS} > .${CONFIG.COMPONENT_CLASS} > :first-child`
      ].join(', '))
      .css('max-height', `${$window.innerHeight}px`);
    }


    /**
     * @private
     *
     * Displays the modal backdrop and disables scrolling on <body>.
     *
     * @return {promise} - Resolves when any animations are complete.
     */
    function showBackdrop () {
      return $q(resolve => {
        // [1] Capture the current y-offset of the page. This must be done
        // before applying the 'modal open' class.
        yOffset = $('body').scrollTop();

        // [2] Apply padding equal to the user's scrollbar width to <body>.
        applyStyle({
          'padding-right': `${scrollbarWidth}px`
        }, body);

        // [3] Add the active backdrop class.
        $rootScope.$applyAsync(() => {
          $animate.addClass(backdropEl, CONFIG.BACKDROP_ACTIVE_CLASS, {
            removeClass: CONFIG.BACKDROP_INACTIVE_CLASS,
            from: {visibility: 'hidden'},
            to: {visibility: 'visible'}
          })
          .then(() => {
            // [4] Add the appropriate body active class and set a top offset.
            body.addClass(CONFIG.BODY_ACTIVE_CLASS);

            // [5] On mobile devices, we need to continuously resize the inner
            // modal element so that its height does not exceed the window's
            // innerHeight property. If this is not done, then the modal may
            // extend to the bottom of the screen behind the browser's chrome,
            // making some of its content and possibly any Cancel/OK buttons
            // inaccesible to the user.
            mobileOnly(() => {
              // Offset the page by yOffset.
              $('body').css('top', `${-yOffset}px`);

              // Resize once, and on subsequent window resizes.
              $window.addEventListener('resize', mobileResizeHandler, false);
              $window.addEventListener('orientationchange', mobileResizeHandler, false);
            });

            resolve();
          });
        });
      });
    }


    /**
     * @private
     *
     * Hides the modal backdrop and restores scrolling on <body>.
     *
     * @return {promise} - Resolves when any animations are complete.
     */
    function hideBackdrop () {
      return $q(resolve => {
        // [1] Add the backdrop inactive class.
        $animate.addClass(backdropEl, CONFIG.BACKDROP_INACTIVE_CLASS, {
          removeClass: CONFIG.BACKDROP_ACTIVE_CLASS,
          from: {visibility: 'visible'},
          to: {visibility: 'hidden'}
        })
        .then(() => {
          // [2] Remove the body active class.
          $('body').removeClass(CONFIG.BODY_ACTIVE_CLASS);

          // [3] Apply padding equal to the user's scrollbar width to <body>.
          applyStyle({
            'padding-right': ''
          }, body);

          // [4] On mobile devices, reset y-offset, and de-register the resize
          // handlers.
          mobileOnly(() => {
            $('body').css('top', '').scrollTop(yOffset);
            $window.removeEventListener('resize', mobileResizeHandler, false);
            $window.removeEventListener('orientationchange', mobileResizeHandler, false);
          });

          resolve();
        });
      });
    }


    /**
     * @private
     *
     * Creates a template string for a modal based on options used.
     *
     * Supports the following schemes:
     * - Using a template string.
     * - Using a templateUrl.
     * - Using a component.
     *
     * @param {object} options - Options object used to create the modal.
     * @return {promise<string>} - Resolves with a template string.
     */
    function getTemplate (options) {
      if (options.component) {
        // Ensure the component is registered.
        if ($injector.has(options.component + 'Directive')) {
          // Convert the component's name to kebab case.
          const kebabName = kebabString(options.component);

          // Create a string of attribute names and values for each property in "locals".
          const attrs = options.locals && Object.keys(options.locals).map(local => {
            return `${kebabString(local)}="locals.${local}"`;
          }).join(' ');

          // Create a template for the component, injecting all locals and $close method.
          return $q.when(`
            <${kebabName} class="${CONFIG.COMPONENT_CLASS}"
              $close="$close"
              ${attrs}>
            </${kebabName}>
          `);
        } else {
          // Reject if the component could not be found.
          return $q.reject(new Error(`Unknown component: "${options.component}".`));
        }
      } else if (options.templateUrl) {
        return $templateRequest(options.templateUrl);
      } else if (options.template) {
        return $q.resolve(options.template);
      } else {
        // If no template option was provided, reject.
        return $q.reject(new Error('Provide a "component", "templateUrl", or "template" for modals.'));
      }
    }


    /**
     * @private
     *
     * Removes a modal from the registry. If the last modal has been removed,
     * hides the backdrop.
     *
     * @param  {object} modal - Modal instance to unregister.
     */
    function unregisterModal (modal) {
      // Remove modal instance from registry.
      modalRegistry = modalRegistry.filter(instance => instance.$id !== modal.$id);

      if (!modalRegistry.length) {
        hideBackdrop();
      }
    }


    /**
     * Computes the scrollbar width in the current client.
     *
     * @return {number}
     */
    function calculateScrollbarWidth () {
      const el = angular.element('<div></div>');

      applyStyle({
        width: '100px',
        height: '100px',
        overflow: 'scroll',
        position: 'absolute',
        top: '-9999px'
      }, el);

      body.append(el);

      const scrollbarWidth = el[0].offsetWidth - el[0].clientWidth;

      el.remove();

      return scrollbarWidth;
    }


    // ----- Initialization & Event-Handlers -----------------------------------

    // Compute scrollbar width.
    scrollbarWidth = calculateScrollbarWidth();


    // Attach backdrop to the body.
    body.append(backdropEl);


    // Handle click-out events. If the top-most modal allows click-out, close it.
    backdropEl.on('click', event => {
      const topModal = modalRegistry.slice(-1)[0];

      if (!topModal) {
        return;
      }

      // Valid event targets for click-out.
      const classes = [
        CONFIG.BACKDROP_CLASS,
        CONFIG.COMPONENT_CLASS,
        CONFIG.MODAL_CLASS
      ];

      const hasAnyClass = element => classes.reduce((hasAny, curClass) => {
        return hasAny || element.hasClass(curClass);
      }, false);

      if (hasAnyClass(angular.element(event.target)) && topModal.options.closeOnClick) {
        // This is a classy event!
        topModal.$close(Symbol.for(CONFIG.CLOSE_ON_CLICK));
      }
    });


    // Handle Esc-out events. If the top-most modal allows Esc-out, close it.
    $document.on('keyup', event => {
      const topModal = modalRegistry.slice(-1)[0];

      if (!topModal) {
        return;
      }

      if (event.keyCode === 27 && topModal.options.closeOnEsc) {
        topModal.$close(Symbol.for(CONFIG.CLOSE_ON_ESC));
      }
    });


    // ----- Public Methods ----------------------------------------------------

    /**
     * Opens a new modal using the provided options.
     *
     * @param {object} options - Options object.
     *
     * @param {boolean} [options.closeOnClick=false] - Whether to close the modal
     *   when the user clicks outside of it.
     * @param {boolean} [options.closeOnEsc=false] - Whether to close the modal
     *   when the user presses the escape key.
     * @param {object} [options.locals] - Object containing values or promises, or
     *   an instance of $q.all that will resolve to an object of values. Locals
     *   will be available on component controllers, and in template-based modals
     *   using the "locals" prefix. Ex: {{ $ctrl.foo }}, {{ locals.foo }}
     *
     * Only one of the following options should be provided:
     *
     * @param {string} options.component - Name of an Angular 1.5 component to use.
     * @param {string} options.templateUrl - URL of a template to use.
     * @param {string} options.template - Template string to use.
     *
     * @return {object} - Returns the modal instance.
     */
    function open (options) {
      // Default options.
      options = Object.assign({
        closeOnClick: false,
        closeOnEsc: false
      }, options);


      // [1] Create a new isolate scope to serve as the modal instance.
      const modal = Object.assign($rootScope.$new(true), {
        on (name, listener) {
          modal.$on(name, (event, ...args) => {
            listener(...args);
          });

          return modal;
        }
      });


      // [2] Resolve template and locals.
      $q.all({
        template: getTemplate(options),
        // If locals are already a promise, use $q.when(), otherwise use $q.all().
        locals: options.locals && $q[options.locals.then ? 'when' : 'all'](options.locals)
      })
      .then(promises => {
        const {locals, template} = promises;

        // [3] Compile a new modal element using the scope from above.
        const modalEl = $compile(`
          <aside class="${CONFIG.MODAL_CLASS}">
            ${template}
          </aside>
        `)(modal);

        // [4] Assign options and resolved locals, and the close method to modal
        // scope.
        Object.assign(modal, {
          locals,
          options,
          $close (...args) {
            $rootScope.$applyAsync(() => {
              $animate.leave(modalEl, {
                removeClass: CONFIG.MODAL_OPEN_CLASS,
                addClass: CONFIG.MODAL_CLOSE_CLASS
              })
              .then(() => {
                modal.$emit('close', ...args);
                modal.$destroy();
                unregisterModal(modal);
              })
              .catch(error => {
                modal.$emit('error', error.message);
              });
            });
          }
        });

        // [5] Add the modal to the registry.
        modalRegistry.push(modal);

        // [6] Apply a z-index to the modal equal to the number of modals
        // currently open. This will ensure that modals stack in the order they
        // were opened.
        applyStyle({
          'z-index': modalRegistry.length
        }, modalEl);

        // [7] Show the backdrop. Wait for the ready event to fire to
        // ensure that the selectors used in showBackdrop will be available.
        function prepareModal () {
          return showBackdrop()

          // [8] Once the backdrop is shown, and add the modal to it.
          .then(() => $animate.enter(modalEl, backdropEl, null, {
            addClass: 'open'
          }))

          // [9] Once the animation to show the modal is complete, resize
          // container elements to ensure it fits on the screen.
          .then(mobileResizeHandler);
        }

        if (process.env.NODE_ENV !== 'test') {
          return $q(resolve => {
            $(backdropEl).ready(() => {
              resolve(prepareModal());
            });
          });
        } else {
          // In testing environments, prepare the modal immediately, as DOM
          // elements do not seem to emit the "ready" event.
          return prepareModal();
        }
      })
      .catch(error => {
        modal.$emit('error', error);
      });


      return modal;
    }


    /**
     * Programatically close all modals.
     *
     * @param  {*} reason - Data to resolve with.
     */
    function closeAll (...args) {
      modalRegistry.forEach(instance => instance.$close(...args));
    }


    return {
      open,
      closeAll
    };
  };
});



// WEBPACK FOOTER //
// ./lib/modalService.js